import EventEmitter from './EventEmitter.js';
import Sizes from './Sizes.js';
import { MathUtils } from 'three';

export default class Input extends EventEmitter {
    static instance = null;

    static getInstance() {
        if (!Input.instance) {
            Input.instance = new Input();
        }
        return Input.instance;
    }

    constructor() {
        if (Input.instance) {
            return Input.instance;
        }
        
        super();
        Input.instance = this;

        this.sizes = Sizes.getInstance();

        // Raw mouse/touch position (initialized off-screen)
        this.x = -1;
        this.y = -1;
        this.prevX = -1;
        this.prevY = -1;

        // Movement speed (delta)
        this.deltaX = 0;
        this.deltaY = 0;

        // Normalized mouse/touch position (0 to 1)
        this.normX = -1;
        this.normY = -1;

        // Interpolated values
        this.interpolatedX = -1;
        this.interpolatedY = -1;
        this.interpolatedNormX = -1;
        this.interpolatedNormY = -1;

        // Flag to track if pointer has moved
        this.hasMouseMoved = false;

        // Idle state tracking
        this.isIdle = true; // Initially true (pointer is considered idle until first move)
        this.idleTimeout = 3000; // 3 seconds of inactivity to trigger idle state
        this.idleTimer = null;
        this.mouseActivityTime = 0; // Last time pointer was active

        // Interpolation factor (adjust for smoothness)
        this.lerpFactor = 0.1;

        // Touch state tracking
        this.isTouching = false;

        // Add event listeners for mouse
        window.addEventListener('mousemove', this.onMouseMove.bind(this));
        window.addEventListener('mousedown', this.onMouseActivity.bind(this));
        window.addEventListener('click', this.onMouseActivity.bind(this));
        
        // Add event listeners for touch
        window.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        window.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: true });
        window.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
    }

    onMouseMove(event) {
        // Skip if currently touching (avoid conflicting inputs)
        if (this.isTouching) return;
        
        this.onMouseActivity();

        // Set flag on first movement
        if (!this.hasMouseMoved) {
            this.hasMouseMoved = true;
            // Initialize interpolated values to current position to avoid animation from -1,-1
            this.interpolatedX = event.clientX;
            this.interpolatedY = event.clientY;
            this.interpolatedNormX = event.clientX / this.sizes.width;
            this.interpolatedNormY = event.clientY / this.sizes.height;
        }

        // Update raw position
        this.x = event.clientX;
        this.y = event.clientY;

        // Update normalized position
        this.normX = this.x / this.sizes.width;
        this.normY = this.y / this.sizes.height;
    }

    onTouchStart(event) {
        if (event.touches.length > 0) {
            this.isTouching = true;
            
            // Process the first touch point
            const touch = event.touches[0];
            
            // Set flag on first touch
            if (!this.hasMouseMoved) {
                this.hasMouseMoved = true;
                // Initialize interpolated values to current position
                this.interpolatedX = touch.clientX;
                this.interpolatedY = touch.clientY;
                this.interpolatedNormX = touch.clientX / this.sizes.width;
                this.interpolatedNormY = touch.clientY / this.sizes.height;
            }
            
            // Update raw position
            this.x = touch.clientX;
            this.y = touch.clientY;
            
            // Update normalized position
            this.normX = this.x / this.sizes.width;
            this.normY = this.y / this.sizes.height;
            
            this.onMouseActivity();
        }
    }
    
    onTouchMove(event) {
        if (event.touches.length > 0) {
            // Process the first touch point
            const touch = event.touches[0];
            
            // Update raw position
            this.x = touch.clientX;
            this.y = touch.clientY;
            
            // Update normalized position
            this.normX = this.x / this.sizes.width;
            this.normY = this.y / this.sizes.height;
            
            this.onMouseActivity();
        }
    }
    
    onTouchEnd(event) {
        this.isTouching = false;
        this.onMouseActivity();
    }

    onMouseActivity() {
        // Update last activity time
        this.mouseActivityTime = Date.now();

        // If currently idle, trigger the change to active
        if (this.isIdle) {
            this.isIdle = false;
            this.trigger('changeIdle', [this.isIdle]); // Broadcast state change
        }

        // Clear any existing idle timer
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
        }

        // Set a new idle timer
        this.idleTimer = setTimeout(() => {
            this.isIdle = true;
            this.trigger('changeIdle', [this.isIdle]); // Broadcast state change
        }, this.idleTimeout);
    }

    update() {
        // Skip update if pointer hasn't moved yet
        if (!this.hasMouseMoved) return;

        // Calculate delta (speed)
        this.deltaX = this.x - this.prevX;
        this.deltaY = this.y - this.prevY;

        // Update previous position
        this.prevX = this.x;
        this.prevY = this.y;

        // Interpolate raw position
        this.interpolatedX = MathUtils.lerp(this.interpolatedX, this.x, this.lerpFactor);
        this.interpolatedY = MathUtils.lerp(this.interpolatedY, this.y, this.lerpFactor);

        // Interpolate normalized position
        this.interpolatedNormX = MathUtils.lerp(this.interpolatedNormX, this.normX, this.lerpFactor);
        this.interpolatedNormY = MathUtils.lerp(this.interpolatedNormY, this.normY, this.lerpFactor);
    }

    dispose() {
        window.removeEventListener('mousemove', this.onMouseMove.bind(this));
        window.removeEventListener('mousedown', this.onMouseActivity.bind(this));
        window.removeEventListener('click', this.onMouseActivity.bind(this));
        
        // Remove touch event listeners
        window.removeEventListener('touchstart', this.onTouchStart.bind(this));
        window.removeEventListener('touchmove', this.onTouchMove.bind(this));
        window.removeEventListener('touchend', this.onTouchEnd.bind(this));
        
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
        }
    }
} 